/**
 * The photo VIEW MODEL — one truthful list from two sources (M5 / S05-04).
 *
 * A photo exists in two places at once for most of its life: a durable local
 * staging record holding the verified-clean bytes, and — from the `attached`
 * stage onwards — a `capture_photo` row on the server. **They are joined on
 * `client_photo_uuid`**, which is exactly the key that exists so a retry
 * produces one photo rather than two. Nothing here is keyed on position or on
 * arrival order, because both change and neither identifies a photo.
 *
 * WHY THIS IS PURE, AND SEPARATE FROM THE SCREEN: the interesting part of the
 * photo UI is not the markup, it is deciding which of four words to put under
 * a thumbnail. Every one of those decisions is a claim about where Brandon's
 * photo actually is, and PT §20 does not allow the product to overstate one.
 * A pure function means each claim is directly testable.
 *
 * THE FOUR STATES, AND WHAT EACH ONE PROMISES:
 *
 *   delivered  — the SERVER confirmed the full-resolution archive
 *                (`archive_uploaded_at` is set, D-028). Only server truth
 *                produces this word. Nothing local can claim it.
 *   uploading  — the delivery queue reports it is working on THIS photo right
 *                now. The queue is sequential, so at most one photo per owner
 *                is ever in this state.
 *   pending    — staged, verified-clean, waiting its turn.
 *   failed     — the last attempt recorded a failure, or the archive was never
 *                confirmed and this device no longer holds the bytes.
 *
 * The last case is the one worth reading twice. It happens after an explicit
 * sign-out during delivery (D-027): the staged bytes are purged, and a row may
 * survive with `archive_uploaded_at` still NULL. That photo is NOT pending —
 * nothing on this device can finish it — and it is NOT delivered. It is a
 * failure that Retry cannot repair, so it reports `retryable: false` and the
 * screen offers no button that would do nothing.
 */

import type { CapturePhotoRow, PhotoClass } from '../database.types';
import type { StagedPhoto } from './photo-staging';

export type PhotoDeliveryState = 'pending' | 'uploading' | 'failed' | 'delivered';

export interface CapturePhotoView {
  /** `client_photo_uuid` — stable from staging through delivery. */
  readonly key: string;
  readonly photoClass: PhotoClass;
  readonly state: PhotoDeliveryState;
  /**
   * The `capture_photo` row id, or null before attach has landed.
   *
   * Removal and reclassification both need it: they act on the SERVER record,
   * and a photo that has no server record yet has nothing for them to act on.
   */
  readonly serverPhotoId: string | null;
  readonly row: CapturePhotoRow | null;
  readonly staged: StagedPhoto | null;
  /** Safe to show. Never a coordinate, never a token (S05-03's rule). */
  readonly failure: string | null;
  /** False when a failure exists that retrying cannot repair. */
  readonly retryable: boolean;
  readonly sourceMime: 'image/jpeg' | 'image/png';
  /** 1–8 or null. The ARCHIVE depends on it; derivatives are already upright. */
  readonly orientation: number | null;
  readonly position: number | null;
}

/**
 * The sentence shown for a photo whose archive can no longer be completed from
 * this device. Exported so the screen and its tests use one wording.
 */
export const ARCHIVE_UNFINISHABLE =
  'The full-size copy was not finished, and this device no longer has it.';

export function buildCapturePhotoViews(input: {
  readonly captureId: string;
  /** ACTIVE rows only — removed photos never reach this function. */
  readonly rows: readonly CapturePhotoRow[];
  /** Every staged photo the owner holds, across Captures. */
  readonly staged: readonly StagedPhoto[];
  /** What the delivery queue reports it is working on, or null when idle. */
  readonly activeClientPhotoUuid: string | null;
}): CapturePhotoView[] {
  const { captureId, rows, staged, activeClientPhotoUuid } = input;

  // This Capture only. The staging store is owner-scoped, not Capture-scoped,
  // because the budget it feeds is device-wide (D-026) — so the filter belongs
  // here rather than in the store.
  const mine = staged.filter((record) => record.captureId === captureId);
  const stagedByUuid = new Map(mine.map((record) => [record.clientPhotoUuid, record]));

  const views: CapturePhotoView[] = [];

  for (const row of rows) {
    const record = stagedByUuid.get(row.client_photo_uuid) ?? null;
    stagedByUuid.delete(row.client_photo_uuid);
    views.push({
      key: row.client_photo_uuid,
      // SERVER TRUTH WINS FOR THE CLASS. Reclassification writes to the row,
      // and the staged record — written once at staging — would otherwise
      // show a stale Before/After after a reclassification.
      photoClass: row.photo_class,
      state: stateForRow(row, record, activeClientPhotoUuid),
      serverPhotoId: row.id,
      row,
      staged: record,
      failure: failureForRow(row, record),
      retryable: record !== null,
      sourceMime: row.source_mime,
      orientation: row.source_orientation,
      position: row.position,
    });
  }

  // Staged photos with no row yet — they have not reached attach. Oldest
  // first, matching the queue's own order, so the list reads in the order the
  // uploads will actually happen.
  const unrowed = [...stagedByUuid.values()].sort((a, b) => a.stagedAtMs - b.stagedAtMs);
  for (const record of unrowed) {
    views.push({
      key: record.clientPhotoUuid,
      photoClass: record.photoClass,
      state: stateForStaged(record, activeClientPhotoUuid),
      serverPhotoId: null,
      row: null,
      staged: record,
      failure: record.lastFailure,
      retryable: true,
      sourceMime: record.archive.mime,
      orientation: record.orientation,
      position: null,
    });
  }

  return views;
}

function stateForStaged(
  record: StagedPhoto,
  activeClientPhotoUuid: string | null,
): PhotoDeliveryState {
  // A failure the queue recorded outranks "it is queued" — the photo is
  // queued AND the last attempt did not work, and the second is the thing
  // Brandon needs to know.
  if (record.lastFailure !== null) return 'failed';
  if (record.clientPhotoUuid === activeClientPhotoUuid) return 'uploading';
  return 'pending';
}

function stateForRow(
  row: CapturePhotoRow,
  record: StagedPhoto | null,
  activeClientPhotoUuid: string | null,
): PhotoDeliveryState {
  // DELIVERED IS A SERVER FACT AND ONLY A SERVER FACT (D-028). It is checked
  // first so no local state can contradict it: a stale staged record left
  // behind by a failed cleanup must not make a delivered photo look pending.
  if (row.archive_uploaded_at !== null) return 'delivered';
  if (record !== null) return stateForStaged(record, activeClientPhotoUuid);
  // Row exists, archive unconfirmed, and the bytes are gone from this device.
  return 'failed';
}

function failureForRow(row: CapturePhotoRow, record: StagedPhoto | null): string | null {
  if (row.archive_uploaded_at !== null) return null;
  if (record !== null) return record.lastFailure;
  return ARCHIVE_UNFINISHABLE;
}

/** Live photos of one class, in the order they should be shown. */
export function viewsOfClass(
  views: readonly CapturePhotoView[],
  photoClass: PhotoClass,
): CapturePhotoView[] {
  return views.filter((view) => view.photoClass === photoClass);
}
